/**
 * WHAT THE MAINTENANCE SCREEN SAYS ABOUT WHEN, AND ABOUT A WINDOW'S STATE.
 *
 * Kept apart from `Maintenance.tsx` so the two rules the module walk found broken (30 Oct 2026) are
 * testable without rendering the screen:
 *
 *   · the calendar opened on September on 30 October, on the next window not yet started or else the
 *     last one read. It opens on the month a Manila operator is standing in; the arrows walk from there.
 *   · four windows whose times had all passed read "Scheduled" under a "Windows ahead 0" tile. A window
 *     still recorded as scheduled whose planned end has passed is shown as past and not marked
 *     complete: nothing marks a window complete by itself, so it stays as somebody left it.
 */

import { codeAsWords } from '../../lib/audit-words.ts';

const PHT = 'Asia/Manila';

const calendarParts = new Intl.DateTimeFormat('en-PH', {
  timeZone: PHT,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The Manila month an instant falls in: the month the calendar opens on. */
export function openingMonth(now: Date): { year: number; month: number } {
  const parts = calendarParts.formatToParts(now);
  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { year: pick('year'), month: pick('month') };
}

type Tone = 'accent' | 'neutral' | 'outline' | 'quiet';

const STATUS: Record<string, { label: string; tone: Tone }> = {
  SCHEDULED: { label: 'Scheduled', tone: 'outline' },
  PAST_UNMARKED: { label: 'Past, not marked complete', tone: 'quiet' },
  IN_PROGRESS: { label: 'In progress', tone: 'accent' },
  RUNNING_LATE: { label: 'In progress, past its planned end', tone: 'quiet' },
  COMPLETED: { label: 'Completed', tone: 'accent' },
  CANCELLED: { label: 'Cancelled', tone: 'quiet' },
};

/**
 * The status a window is SHOWN under: the recorded one, except a scheduled window whose planned end
 * has passed (`PAST_UNMARKED`), or an in-progress one past its planned end (`RUNNING_LATE`: nothing
 * closes a window by itself, so it is still running). The chips group and filter by this, so a chip's count is the
 * rows it shows.
 */
export function shownStatus(w: { status: string; planned_end: string }, now: number = Date.now()): string {
  if (w.status === 'SCHEDULED' && Date.parse(w.planned_end) < now) return 'PAST_UNMARKED';
  if (w.status === 'IN_PROGRESS' && Date.parse(w.planned_end) < now) return 'RUNNING_LATE';
  return w.status;
}

/** A shown status in words; one this screen does not know is written as words, never as the code. */
export function statusWords(shown: string): { label: string; tone: Tone } {
  return STATUS[shown] ?? { label: codeAsWords(shown), tone: 'neutral' };
}
